import { useContext, useEffect, useState } from 'react'
import { CodePanel, RiskDisclaimer, type CodeHighlightedLine } from '@/components'
import { tokenize } from '../../lib/shikiHighlighter'
import { ThemeContext } from '../../providers/ThemeProvider'
import type { BotCode } from '../api'

const STORAGE_KEY = 'risk-disclaimer-acknowledged'

interface BotBacktestCodeProps {
  currentCode?: BotCode | null
  hasCode?: boolean
}

function readAcknowledged(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

export default function BotBacktestCode({ currentCode, hasCode = false }: BotBacktestCodeProps) {
  const themeCtx = useContext(ThemeContext) as { theme?: string } | undefined
  const isDark = themeCtx?.theme === 'dark'

  const code = currentCode?.code ?? ''
  const isEmpty = !hasCode || !currentCode

  const [highlightedLines, setHighlightedLines] = useState<CodeHighlightedLine[] | undefined>(
    undefined,
  )

  useEffect(() => {
    if (!code) return
    let cancelled = false
    tokenize(code, isDark ? 'github-dark' : 'github-light')
      .then((tokens) => {
        if (!cancelled) setHighlightedLines(tokens)
      })
      .catch((err) => {
        console.error('Failed to tokenize code:', err)
      })
    return () => {
      cancelled = true
    }
  }, [code, isDark])

  // Open the disclaimer on first mount when code exists and the user hasn't
  // acknowledged yet. The parent keys this component by `currentCode?.cid`, so
  // a fresh mount happens on every Code-tab entry and on code-identity changes
  // (e.g., when a freshly generated code arrives via SSE while the tab is
  // already visible), giving us the "re-open until acknowledged" behavior.
  const [riskOpen, setRiskOpen] = useState(
    () => !isEmpty && !readAcknowledged(),
  )

  const handleAcknowledge = () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'true')
    } catch {
      // Ignore storage errors (private mode, etc.) - acknowledgement just won't persist.
    }
  }

  return (
    <CodePanel
      code={code}
      highlightedLines={highlightedLines}
      isEmpty={isEmpty}
      footerSlot={
        !isEmpty && (
          <RiskDisclaimer
            open={riskOpen}
            onAcknowledge={handleAcknowledge}
            onOpenChange={setRiskOpen}
          />
        )
      }
    />
  )
}
